import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import type { WeldAnalyticsBundle } from '../types/weld';
import { countByField } from '../utils/systemAggregates';
import { isWeldIqUnavailable, sanitizeWeldIqUserMessage } from '../utils/weldIqEmptyState';
import { AnalyticsSegmentedControl } from './shared/AnalyticsSegmentedControl';
import { WeldFilterPanel, type WeldFilterSelection, type JobOptionGrouped } from './WeldFilterPanel';
import { StatisticsPanel } from './StatisticsPanel';
import { WeldInsightsPanel } from './WeldInsightsPanel';
import { WeldIqBundleStatusBanners } from './WeldIqBundleStatusBanners';
import { WeldVisualDashboard } from './dashboard/WeldVisualDashboard';
import { sectionTitleClass } from './dashboard/dashboardTokens';
import type { OverviewWeldRow } from './WeldOverviewTable';
import type { PipeOption } from '../../ndt/components/FilterPanel';
import type { PipeJobRef } from '../utils/jobNumbersFromSetup';
import type { PendingWeldDrillDown } from './dashboard/drilldown/drillDownTypes';

type MainTab = 'overview' | 'statistics' | 'insights';

/** NDT blocks Combined inserts around the weld page. Weld Analytics leaves these unset. */
export type WeldDetailExtras = {
  /** NDT controls rendered inside the single weld filter row. */
  filterFields?: ReactNode;
  /** Overview only, between the filter bar and the weld dashboard. */
  overviewLead?: ReactNode;
  /** End of the Overview tab, after the weld dashboard. */
  overviewTrail?: ReactNode;
  /** Extra Statistics sub-tabs. Combined uses these for NDT statistics and welder performance. */
  statisticsTabs?: { label: string; content: ReactNode }[];
  /** End of AI Insights, after the weld insights. */
  insightsTrail?: ReactNode;
  /**
   * Combined Overview gate: NDT side status so we can joint-skeleton until weld+NDT ready.
   * Soft-degrade when one side errors.
   */
  overviewNdt?: {
    status: 'idle' | 'loading' | 'ready' | 'empty' | 'error';
    error?: string | null;
    onRetry?: () => void;
  };
};

function OverviewJointSkeleton() {
  return (
    <div className="space-y-4" data-testid="overview-joint-skeleton" aria-busy>
      <div className="h-4 w-1/3 animate-pulse rounded bg-[#E2E5EA]" />
      <div className="h-24 w-full animate-pulse rounded-xl bg-[#E2E5EA]" />
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="h-20 animate-pulse rounded-xl bg-[#E2E5EA]" />
        <div className="h-20 animate-pulse rounded-xl bg-[#E2E5EA]" />
        <div className="h-20 animate-pulse rounded-xl bg-[#E2E5EA]" />
      </div>
      <div className="h-40 w-full animate-pulse rounded-xl bg-[#E2E5EA]" />
      <div className="h-4 w-1/4 animate-pulse rounded bg-[#E2E5EA]" />
      <div className="h-48 w-full animate-pulse rounded-xl bg-[#E2E5EA]" />
    </div>
  );
}

const MAIN_TABS: { id: MainTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'statistics', label: 'Statistics' },
  { id: 'insights', label: 'AI Insights' },
];

export type { PendingWeldDrillDown };

interface WeldDetailViewProps {
  bundle: WeldAnalyticsBundle | null;
  filterSelection: WeldFilterSelection;
  jobOptions: JobOptionGrouped[];
  pipeSelected: boolean;
  onFilterChange: (next: Partial<WeldFilterSelection>) => void;
  onApplyFilters: () => void;
  filtersDirty?: boolean;
  appliedJobNumbers?: string[];
  appliedJobRefs?: PipeJobRef[];
  appliedPipeIds?: string[];
  isLoading: boolean;
  isApplying?: boolean;
  hasAppliedData?: boolean;
  /** Production-blocking Overview error (null when production rows exist). */
  error: string | null;
  /** Unexpected WeldIQ hard error for Statistics / Insights (not soft pipeline gaps). */
  weldIqError?: string | null;
  health: Record<string, unknown> | null;
  isAdmin: boolean;
  isTriggeringPipeline: boolean;
  pipelineResult: Record<string, unknown> | null;
  pipelineError: string | null;
  onTriggerPipeline: () => void;
  productionWeldData: OverviewWeldRow[];
  productionWeldDataLoading: boolean;
  productionStatusCalculationMethod?: string;
  projectId: number | null;
  pipeId: string | null;
  pipeIds?: string[];
  pipeOptions?: PipeOption[];
  canEditNdt?: boolean;
  externalPendingDrillDown?: PendingWeldDrillDown | null;
  onExternalPendingDrillDownConsumed?: () => void;
  detailExtras?: WeldDetailExtras;
  /** Controlled from AnalyticsShell Filters toggle. Default closed. */
  filtersOpen?: boolean;
  onFiltersOpenChange?: (open: boolean) => void;
}

export const WeldDetailView = ({
  bundle,
  filterSelection,
  jobOptions,
  pipeSelected,
  onFilterChange,
  onApplyFilters,
  filtersDirty = false,
  appliedJobNumbers = [],
  appliedJobRefs = [],
  appliedPipeIds = [],
  isLoading,
  isApplying = false,
  hasAppliedData = false,
  error,
  weldIqError = null,
  health,
  isAdmin,
  isTriggeringPipeline,
  pipelineResult,
  pipelineError,
  onTriggerPipeline,
  productionWeldData,
  productionWeldDataLoading,
  productionStatusCalculationMethod = 'Instantaneous',
  projectId,
  pipeId,
  pipeIds = [],
  pipeOptions = [],
  canEditNdt = false,
  externalPendingDrillDown = null,
  onExternalPendingDrillDownConsumed,
  detailExtras,
  filtersOpen = false,
  onFiltersOpenChange,
}: WeldDetailViewProps) => {
  const [activeTab, setActiveTab] = useState<MainTab>('overview');
  const [pendingDrillDown, setPendingDrillDown] = useState<PendingWeldDrillDown | null>(null);
  const [drillDownReturnTab, setDrillDownReturnTab] = useState<'statistics' | 'insights' | null>(
    null,
  );
  const wasApplyingRef = useRef(false);

  useEffect(() => {
    if (!externalPendingDrillDown) return;
    setPendingDrillDown({
      ...externalPendingDrillDown,
      variant: externalPendingDrillDown.variant ?? 'compliance',
    });
    setActiveTab('overview');
    onExternalPendingDrillDownConsumed?.();
  }, [externalPendingDrillDown, onExternalPendingDrillDownConsumed]);

  // Auto-collapse Filters after a successful Apply; keep open on error.
  useEffect(() => {
    if (wasApplyingRef.current && !isApplying) {
      if (!error) {
        onFiltersOpenChange?.(false);
      }
    }
    wasApplyingRef.current = isApplying;
  }, [isApplying, error, onFiltersOpenChange]);

  const weldingSystems = useMemo(() => {
    if (!bundle?.welds) return [];
    return Object.keys(countByField(bundle.welds, (w) => w.welding_system || 'Unknown')).sort();
  }, [bundle?.welds]);

  const passFamilies = useMemo(() => {
    if (!bundle?.anomalies) return [];
    return Object.keys(
      countByField(bundle.anomalies, (a) => a.pass_family || a.pass_name || 'Unknown'),
    ).sort();
  }, [bundle?.anomalies]);

  const openWeldDrillDown = (target: PendingWeldDrillDown) => {
    if (activeTab === 'statistics' || activeTab === 'insights') {
      setDrillDownReturnTab(activeTab);
    }
    setPendingDrillDown({ ...target, variant: target.variant ?? 'compliance' });
    setActiveTab('overview');
  };

  const clearPendingDrillDown = () => setPendingDrillDown(null);

  const handleDrillDownClose = () => {
    if (drillDownReturnTab) {
      setActiveTab(drillDownReturnTab);
      setDrillDownReturnTab(null);
    }
  };

  const hasProductionData = productionWeldData.length > 0;
  /** Combined host passes detailExtras; prefer neutral "Analytics" copy there. */
  const productLabel = detailExtras ? 'Analytics' : 'Weld Analytics';
  const ndtStatisticsFallback =
    activeTab === 'statistics' ? detailExtras?.statisticsTabs : undefined;

  const renderTabContent = () => {
    if (activeTab === 'overview') {
      const ndt = detailExtras?.overviewNdt;
      const weldLoading = Boolean(
        pipeSelected && (isApplying || (isLoading && !hasAppliedData) || productionWeldDataLoading),
      );
      const ndtLoading = ndt != null && (ndt.status === 'loading' || ndt.status === 'idle');
      const weldFailed = Boolean(error && !hasAppliedData && !weldLoading);
      const ndtFailed = ndt?.status === 'error';
      const bothLoading = detailExtras != null && (weldLoading || ndtLoading) && !weldFailed && !ndtFailed;

      if (bothLoading) {
        return <OverviewJointSkeleton />;
      }

      const showWeld = detailExtras == null || !ndtLoading || weldFailed || ndtFailed || ndt?.status === 'ready' || ndt?.status === 'empty';
      const showNdtTrail =
        detailExtras?.overviewTrail != null &&
        (!weldLoading || weldFailed || ndtFailed || hasAppliedData);

      return (
        <>
          {showWeld ? (
            <>
              {detailExtras?.overviewLead}
              <WeldVisualDashboard
                bundle={bundle}
                productionWeldData={productionWeldData}
                productionWeldDataLoading={productionWeldDataLoading}
                productionStatusCalculationMethod={productionStatusCalculationMethod}
                appliedJobNumbers={appliedJobNumbers}
                appliedJobRefs={appliedJobRefs}
                appliedPipeIds={appliedPipeIds}
                isLoading={isLoading}
                isRefreshing={isApplying}
                hasAppliedData={hasAppliedData}
                error={error}
                pipeSelected={pipeSelected}
                projectId={projectId}
                pipeId={pipeId}
                pipeIds={pipeIds}
                pipeOptions={pipeOptions}
                canEditNdt={canEditNdt}
                pendingDrillDown={pendingDrillDown}
                onPendingDrillDownConsumed={clearPendingDrillDown}
                onDrillDownClose={handleDrillDownClose}
              />
            </>
          ) : null}
          {ndtFailed && !weldLoading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
              <p className="text-readable-red">{ndt?.error || 'NDT data failed to load.'}</p>
              {ndt?.onRetry ? (
                <button
                  type="button"
                  className="mt-2 font-medium text-blueprint-blue underline"
                  onClick={ndt.onRetry}
                >
                  Retry NDT
                </button>
              ) : null}
            </div>
          ) : null}
          {showNdtTrail && !ndtFailed ? detailExtras?.overviewTrail : null}
        </>
      );
    }
    if (!bundle) return null;
    const statusBanners = (
      <WeldIqBundleStatusBanners bundle={bundle} hasProductionData={hasProductionData} />
    );
    switch (activeTab) {
      case 'statistics':
        return (
          <div className="space-y-4">
            {statusBanners}
            <StatisticsPanel
              bundle={bundle}
              appliedJobNumbers={appliedJobNumbers}
              onOpenWeldDrillDown={openWeldDrillDown}
              extraTabs={detailExtras?.statisticsTabs}
            />
          </div>
        );
      case 'insights':
        return (
          <div className="space-y-4">
            {statusBanners}
            <section aria-label="Weld insights" className="space-y-3">
              <h2 className={sectionTitleClass}>Weld insights</h2>
              <WeldInsightsPanel bundle={bundle} onOpenWeldDrillDown={openWeldDrillDown} />
            </section>
          </div>
        );
      default:
        return null;
    }
  };

  const renderNonOverviewState = () => {
    if (!pipeSelected) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-text-grey">Select a pipe size to load {productLabel}.</p>
        </div>
      );
    }
    if (appliedJobNumbers.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-text-grey">
            Select a pipe size to load jobs, then open Filters if you need to refine Job #.
          </p>
        </div>
      );
    }
    if (isLoading && !hasAppliedData && !bundle) {
      return (
        <div className="flex items-center justify-center gap-2 py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-hero-red" />
          <span className="text-lg text-text-grey">Loading {productLabel}…</span>
        </div>
      );
    }
    // Soft pipeline gaps: show banner + empty panels via renderTabContent.
    if (isWeldIqUnavailable(bundle)) {
      return null;
    }
    if (weldIqError && !bundle) {
      const calm = sanitizeWeldIqUserMessage(weldIqError) || weldIqError;
      return (
        <div className="flex items-center justify-center py-12">
          <div className="max-w-lg text-center">
            <p className="text-lg text-readable-red">{calm}</p>
          </div>
        </div>
      );
    }
    if (!bundle && !isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <p className="text-text-grey">No data returned for the selected jobs.</p>
        </div>
      );
    }
    return null;
  };

  const isOverviewTab = activeTab === 'overview';
  const nonOverviewBlock = !isOverviewTab ? renderNonOverviewState() : null;
  const tabBody = isOverviewTab ? renderTabContent() : (nonOverviewBlock ?? renderTabContent());
  const contentBusy = isApplying || (isLoading && !hasAppliedData);

  return (
    <div className="space-y-5">
      <WeldFilterPanel
        jobOptions={jobOptions}
        weldingSystems={weldingSystems}
        passFamilies={passFamilies}
        selection={filterSelection}
        onChange={onFilterChange}
        onApply={onApplyFilters}
        isApplying={isApplying}
        pipeSelected={pipeSelected}
        filtersDirty={filtersDirty}
        appliedJobNumbers={appliedJobNumbers}
        filtersOpen={filtersOpen}
        extraFields={detailExtras?.filterFields}
      />

      <div
        className={clsx(
          'rounded-2xl border border-[#E2E5EA] bg-white p-6 shadow-sm transition-opacity',
          contentBusy && 'pointer-events-none opacity-50',
        )}
        aria-busy={contentBusy || undefined}
      >
        <div className="mb-4 border-b border-[#E2E5EA] pb-4">
          <AnalyticsSegmentedControl
            ariaLabel={detailExtras ? 'Analytics view' : 'Weld analytics view'}
            tabs={MAIN_TABS.map((tab) => ({ id: tab.id, label: tab.label }))}
            activeId={activeTab}
            onChange={(id) => setActiveTab(id as MainTab)}
          />
        </div>
        <div className="space-y-6">
          {tabBody}
          {/* Soft-degrade: when welding stats cannot render, still show NDT extra tabs once (not via StatisticsPanel). */}
          {nonOverviewBlock != null && ndtStatisticsFallback
            ? ndtStatisticsFallback.map((tab) => <div key={tab.label}>{tab.content}</div>)
            : null}
          {activeTab === 'insights' ? detailExtras?.insightsTrail : null}
        </div>
      </div>
    </div>
  );
};
